import axios from 'axios';
import { AxiosError } from 'axios';
const axiosInstance = axios;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
}

export interface ApiUser {
  id: number;
  guid: string;
  name: string;
  email: string;
  access_token: string;
  personal_key: string;
  industries: string;
  companies: string;
  created_at: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  industries: number[] | string;
  companies: number[] | string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  industries?: number[] | string;
  companies?: number[] | string;
}

class ApiService {
  private handleError(error: any): never {
    if (error) {
      const status = error.response?.status;
      let errorMessage = `HTTP ${status}`;

      switch (status) {
        case 400:
          errorMessage = 'Dados inválidos - nome ou email ausente';
          break;
        case 404:
          errorMessage = 'Usuário não encontrado';
          break;
        case 409:
          errorMessage = 'E-mail já cadastrado';
          break;
        case 500:
          errorMessage = 'Erro interno do servidor';
          break;
        default:
          errorMessage = error.response?.data?.error || error.message || errorMessage;
      }

      throw new Error(errorMessage);
    }

    throw error;
  }

  async getUsers(): Promise<ApiUser[]> {
    try {
      const response = await axiosInstance.get<ApiResponse<ApiUser[]>>('/api/users');
      return response.data.data || [];
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      this.handleError(error);
    }
  }

  async createUser(userData: CreateUserRequest): Promise<ApiUser> {
    try {
      const response = await axiosInstance.post<ApiResponse<{ id?: number }>>('/api/users', userData);

      if (response.data.success === false) {
        throw new Error('Falha ao criar usuário');
      }

      const users = await this.getUsers();
      const newUser = users.find(user => user.email === userData.email);

      if (!newUser) {
        throw new Error('Usuário criado mas não encontrado na lista');
      }

      return newUser;
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      this.handleError(error);
    }
  }

  async updateUser(guid: string, userData: UpdateUserRequest): Promise<ApiUser> {
    try {
      const response = await axiosInstance.put<ApiResponse<unknown>>(`/api/users/${guid}`, userData);

      if (response.data.success === false) {
        throw new Error('Falha ao atualizar usuário');
      }

      const users = await this.getUsers();
      const updatedUser = users.find(user => user.guid === guid);

      if (!updatedUser) {
        throw new Error('Usuário atualizado mas não encontrado na lista');
      }

      return updatedUser;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      this.handleError(error);
    }
  }

  async deleteUser(guid: string): Promise<void> {
    try {
      await axiosInstance.delete(`/api/users/${guid}`);
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      this.handleError(error);
    }
  }
}

export const apiService = new ApiService();
